/**
 * Auth Context - provides auth state and actions.
 * Redux remains the single source of truth; this context wires up
 * the token refresh service and expiry warning UX.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useAppSelector } from "../redux/hooks";
import {
  scheduleFromTokens,
  clearAllTimers,
  setExpiryWarningCallback,
  handleStayLoggedIn as doStayLoggedIn,
  extendSessionOnActivity,
} from "./services/tokenRefreshService";
import { performLogout } from "./performLogout";
import { LOGOUT_STORAGE_KEY } from "../utils/sessionLogout";
import { toast } from "sonner";

interface AuthContextValue {
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  logout: () => void;
  handleStayLoggedIn: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { accessToken, refreshToken, accessExpiresInSec } = useAppSelector(
    (state) => state.auth
  );

  const handleStayLoggedIn = useCallback(async (): Promise<boolean> => {
    return doStayLoggedIn();
  }, []);

  const logout = useCallback(() => {
    void performLogout({ callLogoutApi: true });
  }, []);

  useEffect(() => {
    if (accessToken && refreshToken) {
      scheduleFromTokens(accessToken, refreshToken, accessExpiresInSec);
    } else {
      clearAllTimers();
    }
    return () => clearAllTimers();
  }, [accessToken, refreshToken, accessExpiresInSec]);

  useEffect(() => {
    setExpiryWarningCallback(() => {
      toast(
        "Your session will expire in 30 seconds. Click here to stay logged in.",
        {
          duration: 31000,
          id: "session-expiry-warning",
          action: {
            label: "Stay Logged In",
            onClick: () => handleStayLoggedIn(),
          },
        }
      );
    });

    return () => setExpiryWarningCallback(null);
  }, [handleStayLoggedIn]);

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === LOGOUT_STORAGE_KEY && e.newValue) {
        void performLogout({
          callLogoutApi: false,
          broadcastMultiTab: false,
        }).then(() => {
          window.location.href = "/";
        });
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    if (!accessToken || !refreshToken) return;
    const onActivity = () => {
      void extendSessionOnActivity();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") onActivity();
    };
    const useCapture = true;
    document.addEventListener("click", onActivity, useCapture);
    document.addEventListener("keydown", onActivity, useCapture);
    document.addEventListener("input", onActivity, useCapture);
    document.addEventListener("paste", onActivity, useCapture);
    document.addEventListener("pointerdown", onActivity, useCapture);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onActivity);
    return () => {
      document.removeEventListener("click", onActivity, useCapture);
      document.removeEventListener("keydown", onActivity, useCapture);
      document.removeEventListener("input", onActivity, useCapture);
      document.removeEventListener("paste", onActivity, useCapture);
      document.removeEventListener("pointerdown", onActivity, useCapture);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onActivity);
    };
  }, [accessToken, refreshToken]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: Boolean(accessToken && refreshToken),
      accessToken: accessToken || null,
      refreshToken: refreshToken || null,
      logout,
      handleStayLoggedIn,
    }),
    [accessToken, refreshToken, logout, handleStayLoggedIn]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
